import axios from 'axios';
import shortid from 'shortid';
import { createBrowserHistory } from 'history';

import WP from 'WP';
import { parseQueryString } from 'helpers';
import { store } from 'store';
import PostFetcher from './PostFetcher';

const defaultTimeout = 65000;

const history = createBrowserHistory();

const api = {
    getMenuByLocation: location => {
        const storeState = store.getState();

        if (storeState.menus[location]) {
            return Promise.resolve({ data: Object.values(storeState.menus[location]) });
        }

        return axios.get(WP.api_base + 'wp-api-menus/v2/menus/' + WP[location + '_id'], {
            params: {
                stamp: shortid.generate(),
            }
        }).then(resp => {
            store.dispatch({ type: 'ADD_MENU_TO_STORE', payload: { location, menu: resp.data } });
            return resp;
        });
    },

    /**
     * Handle page/post previews
     *
     * http://domain.local/?post_type=trip&p=448&preview=true - custom post type preview
     * http://domain.local/?page_id=446&preview=true - page preview
     * http://domain.local/?p=452&preview=true - post preview
     */
    getHomePage: () => {
        const queryParams = parseQueryString(history.location.search);

        if (queryParams.preview === 'true') {
            if (queryParams.post_type === 'tjenester') {
                return api.getDraftById(queryParams.p);
            }
            if (queryParams.page_id) {
                return api.getDraftById(queryParams.page_id);
            }
            if (queryParams.p) {
                return api.getDraftById(queryParams.p);
            }
        }

        // check if page is already in store
        const storeState = store.getState();
        const frontPageId = WP.front_page;
        if (storeState.pages[frontPageId]) {
            return Promise.resolve({ data: storeState.pages[frontPageId] });
        }

        // fetch page from rest api
        return axios.get(WP.api_base + 'wp/v2/pages/' + WP.front_page, {
            params: {
                lang: WP.lang,
                stamp: shortid.generate(),
            }
        }).then(resp => {
            store.dispatch({ type: 'ADD_TO_PAGES', payload: { page: resp.data, key: frontPageId } });
            return resp;
        })
    },

    getPageBySlug: slug => {
        // check if asking for post revision
        const queryParams = parseQueryString(history.location.search);
        if (queryParams.preview === 'true' && queryParams.preview_id) {
            return api.getPostRevisions(queryParams.preview_id);
        }

        // check if page is already in store
        const storeState = store.getState();
        if (storeState.pages[slug]) {
            return Promise.resolve({ data: [storeState.pages[slug]] });
        }

        // fetch page from api
        return axios.get(WP.api_base + 'wp/v2/pages', {
            params: {
                slug: slug,
                lang: WP.lang,
                stamp: shortid.generate(),
            }
        }).then(resp => {
            const page = resp.data[0];
            if (page) {
                // Key by URL slug so Page.js mapStateToProps (match.params.slug) matches after fetch.
                // REST `page.slug` can differ (nested permalinks, Polylang, encoding).
                store.dispatch({ type: 'ADD_TO_PAGES', payload: { page, key: slug } });
                if (page.slug && page.slug !== slug) {
                    store.dispatch({ type: 'ADD_TO_PAGES', payload: { page, key: page.slug } });
                }
            }
            return resp;
        });
    },

    getBlogBySlug: slug => {
        // check if asking for post revision
        const queryParams = parseQueryString(history.location.search);
        if (queryParams.preview === 'true' && queryParams.preview_id) {
            return api.getPostRevisions(queryParams.preview_id);
        }

        // check if page is already in store
        const storeState = store.getState();
        if (storeState.pages[slug]) {
            return Promise.resolve({ data: [storeState.pages[slug]] });
        }

        // fetch page from api
        return axios.get(WP.api_base + 'wp/v2/blog', {
            params: {
                slug: slug.slug
            }
        }).then(resp => {
            const page = resp.data[0];
            if (page) store.dispatch({ type: 'ADD_TO_BLOGS', payload: { page, key: page.slug } });
            return resp;
        });
    },

    getDraftById: id => {
        return axios.get(WP.api_base + 'bjorknes/v2/draft', {
            params: {
                lang: WP.lang,
                id: id,
                stamp: shortid.generate(),
            }
        })
    },

    getPostRevisions: id => {
        return axios.get(WP.api_base + 'wp/v2/pages/' + id + '/revisions/', {
            params: {
                stamp: shortid.generate(),
            }
        });
    },

    importantDatesFetcher: new PostFetcher({
        storeKey: 'importantDates',
        fetchEndpoint: WP.api_base + 'wp/v2/event',
        debug: false,
    }),

    courseFetcher: new PostFetcher({
        storeKey: 'courses',
        fetchEndpoint: WP.api_base + 'wp/v2/kurs',
        debug: false,
    }),

    privateTutoringFetcher: new PostFetcher({
        storeKey: 'privateTutoring',
        fetchEndpoint: WP.api_base + 'wp/v2/privatundervisning',
        debug: false,
    }),

    emneTermFetcher: new PostFetcher({
        storeKey: 'emne',
        fetchEndpoint: WP.api_base + 'wp/v2/emne',
        debug: false,
    }),

    categoryTermFetcher: new PostFetcher({
        storeKey: 'kategori',
        fetchEndpoint: WP.api_base+'wp/v2/kategori',
        debug: false
    }),

    slotFetcher: new PostFetcher({
        storeKey: 'slots',
        fetchEndpoint: WP.api_base + 'wp/v2/slot',
        debug: false,
    }),

    employeesFetcher: new PostFetcher({
        storeKey: 'employees',
        fetchEndpoint: WP.api_base + 'wp/v2/employee',
        debug: false,
    }),

    blogFetcher: new PostFetcher({
        storeKey: 'blog',
        fetchEndpoint: WP.api_base + 'wp/v2/blog',
        debug: false,
    }),

    coursesFetcher: new PostFetcher({
        storeKey: 'courses',
        fetchEndpoint: WP.api_base + 'bjorknes/api/v1/courses',
        debug: false,
    }),

    programsFetcher: new PostFetcher({
        storeKey: 'programs',
        fetchEndpoint: WP.api_base + 'bjorknes/api/v1/programs',
        debug: false,
    }),

    categoriesFetcher: new PostFetcher({
        storeKey: 'categories',
        fetchEndpoint: WP.api_base + 'wp/v2/category',
        debug: false,
    }),

    academicAreasFetcher: new PostFetcher({
        storeKey: 'academicAreas',
        fetchEndpoint: WP.api_base + 'wp/v2/academic-area',
        debug: false,
    }),

    createCustomer: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.post(WP.api_base + 'bjorknes/api/v1/customers', {
            firstName: data.firstName,
            lastName: data.lastName,
            personalNumber: data.personalNumber,
            email: data.email,
            phone: data.phone,
            country: data.country,
            city: data.city,
            street: data.street,
            postalCode: data.postalCode,
            partialObject: data.partialObject,
        });
    },

    updateCustomer: (id, data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.put(WP.api_base + 'bjorknes/api/v1/customers/' + id, {
            firstName: data.firstName,
            lastName: data.lastName,
            personalNumber: data.personalNumber,
            email: data.email,
            phone: data.phone,
            country: data.country,
            city: data.city,
            street: data.street,
            postalCode: data.postalCode,
            partialObject: data.partialObject,
        });
    },

    getTimeScheduler: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;
        return axios.get(WP.api_base + 'bjorknes/api/v1/timeschedule', {
            params: {
                courseOfferings: data
            }
        });
    },

    createTimeScheduler: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;
        return axios.put(WP.api_base + 'bjorknes/api/v1/timeschedule', {
            courseOccasionIds: data.courseOccasionIds,
            opportunityId: data.opportunityId
        });
    },

    createPaymentIntent: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.post(WP.api_base + 'bjorknes/api/v1/payments', {
            opportunityId: data.opportunityId,
            customerId: data.customerId,
            opportunityAmount: data.opportunityAmount
        });
    },

    getOpportunityByToken: (id) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.get(WP.api_base + 'bjorknes/api/v1/opportunities?opportunityToken=' + id);
    },

    createOpportunity: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.post(WP.api_base + 'bjorknes/api/v1/opportunities', {
            customerId: data.customerId,
            educationalGoal: data.educationalGoal,
            attachments: data.attachments,
            comment: data.comment,
            programs: data.programs,
            courses: data.courses,
            transactionId: data.transactionId,
            reservedAmount: data.reservedAmount,
            paymentMethod: data.paymentMethod,
            coupon: data.coupon,
            partialObject: data.partialObject,
        });
    },

    updateOpportunity: (id, data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.put(WP.api_base + 'bjorknes/api/v1/opportunities/' + id, {
            customerId: data.customerId,
            educationalGoal: data.educationalGoal,
            attachments: data.attachments,
            comment: data.comment,
            programs: data.programs,
            courses: data.courses,
            transactionId: data.transactionId,
            reservedAmount: data.reservedAmount,
            paymentMethod: data.paymentMethod,
            coupon: data.coupon,
            partialObject: data.partialObject
        });
    },

    getLoanCalculation: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.post(WP.api_base + 'bjorknes/api/v1/loan', {
            programs: data.programs,
            courses: data.courses
        });
    },

    getTotalCalculation: (data) => {
        axios.defaults.headers.common["X-Wordpress-Nonce"] = WP.nonce;
        axios.defaults.timeout = defaultTimeout;

        return axios.post(WP.api_base + 'bjorknes/api/v1/calculator/total', {
            programs: data.programs,
            courses: data.courses
        });
    },

    getTerms: taxonomy => {
        const storeState = store.getState();
        if (storeState[taxonomy] && storeState[taxonomy].length > 0) {
            return Promise.resolve({ data: Object.values(storeState[taxonomy]) });
        }

        return axios.get(WP.api_base + 'bjorknes/v2/terms', {
            params: {
                taxonomy: taxonomy,
                stamp: shortid.generate(),
            }
        }).then(resp => {
            store.dispatch({ type: 'SET_' + taxonomy.toUpperCase(), payload: resp.data });
            return resp;
        });
    },

    getCalculatorSets: () => {
        const storeState = store.getState();

        if (storeState.calculator.sets.length > 0) {
            return Promise.resolve({ data: storeState.calculator.sets });
        }

        return axios.get(WP.api_base + 'wp/v2/calculator', {
            params: {
                stamp: shortid.generate(),
            }
        }).then(resp => {
            store.dispatch({ type: 'SET_CALCULATOR_SETS', payload: resp.data });
            return resp;
        });
    },

    getTimeSlots: ({ week, year }) => {
        return axios.get(WP.api_base + 'bjorknes/v2/timeslots', {
            params: {
                stamp: shortid.generate(),
                week,
                year,
            }
        });
    },

    bookMeeting: data => {
        return axios.post(WP.api_base + 'bjorknes/v2/booking', {
            data,
            action: 'create'
        });
    },

    updateBooking: data => {
        return axios.post(WP.api_base + 'bjorknes/v2/booking', {
            data,
            action: 'change'
        });
    },

    requestBookingCode: data => {
        return axios.post(WP.api_base + 'bjorknes/v2/booking', {
            data,
            action: 'send_ref'
        });
    },

    getBooking: data => {
        return axios.post(WP.api_base + 'bjorknes/v2/booking', {
            data,
            action: 'get'
        });
    },

    deleteBooking: data => {
        return axios.post(WP.api_base + 'bjorknes/v2/booking', {
            data,
            action: 'delete'
        });
    },

    checkout: (data, details) => {
        return axios.post(WP.api_base + 'bjorknes/v2/checkout', {
            data,
            details
        });
    },

    generateXLSX: ({ programs, yearBorn, additionalPoints, summary }) => {
        return axios.get(WP.api_base + 'bjorknes/v2/xlsx', {
            responseType: 'blob',
            params: {
                stamp: shortid.generate(),
                programs,
                yearBorn,
                additionalPoints,
                summary,
            }
        });
    },

    stripeSetCharge: (token) => {
        return axios.post(WP.api_base + 'bjorknes/stripe/', {
            token: token.id
        }, {
            headers: { 'Content-Type': 'text/plain' }
        });
    },

}

export default api;


// WEBPACK FOOTER //
// ./src/api/api.js